// ===========================================
// Event Photos Panel
// ===========================================
// Displays event gallery with delete, and a Pixabay picker for adding photos.
// Used inside EventDetailPage.

import { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Photo as PhotoIcon,
} from '@mui/icons-material';
import {
  useEventPhotos,
  useDeletePhoto,
  useAddPixabayPhoto,
} from '../../hooks/useEvents';
import { EventPhoto, PixabaySearchResult } from '../../api/events.api';
import ConfirmDialog from '../common/ConfirmDialog';
import EventPixabayPicker from './EventPixabayPicker';

// ===========================================
// MAIN PANEL
// ===========================================

interface EventPhotosPanelProps {
  eventId: string;
}

export default function EventPhotosPanel({ eventId }: EventPhotosPanelProps) {
  const { data: photosRes, isLoading } = useEventPhotos(eventId);
  const deletePhotoMutation = useDeletePhoto();
  const addPhotoMutation = useAddPixabayPhoto();
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const photos: EventPhoto[] = photosRes ?? [];

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    await deletePhotoMutation.mutateAsync({ eventId, photoId: deleteTargetId });
    setDeleteTargetId(null);
  };

  const handleAddToGallery = async (image: PixabaySearchResult, caption: string) => {
    await addPhotoMutation.mutateAsync({
      eventId,
      data: {
        pixabayPageUrl: image.pageUrl,
        imageUrl: image.fullUrl,
        user: image.user,
        caption: caption || undefined,
      },
    });
  };

  return (
    <Box>
      {/* Existing Photos */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Event Photos ({photos.length})
      </Typography>

      {isLoading ? (
        <ImageList cols={4} gap={8} sx={{ mb: 3 }}>
          {[...Array(4)].map((_, i) => (
            <ImageListItem key={i}>
              <Skeleton variant="rectangular" height={120} />
            </ImageListItem>
          ))}
        </ImageList>
      ) : photos.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            mb: 3,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <PhotoIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary" variant="body2">
            No photos added yet
          </Typography>
        </Box>
      ) : (
        <ImageList cols={4} gap={8} sx={{ mb: 3 }}>
          {photos.map((photo) => (
            <ImageListItem key={photo.id} sx={{ borderRadius: 1, overflow: 'hidden' }}>
              <img
                src={photo.imageUrl}
                alt={photo.caption ?? 'Event photo'}
                loading="lazy"
                style={{ height: 120, objectFit: 'cover', width: '100%' }}
              />
              <ImageListItemBar
                subtitle={photo.caption || (photo.pixabayUser ? `by ${photo.pixabayUser}` : '')}
                actionIcon={
                  <Tooltip title="Delete photo">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTargetId(photo.id)}
                      sx={{ color: 'white' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}

      {/* Pixabay Picker */}
      <EventPixabayPicker
        onAddToGallery={handleAddToGallery}
        isAdding={addPhotoMutation.isPending}
      />

      <ConfirmDialog
        open={!!deleteTargetId}
        title="Delete Photo"
        message="Are you sure you want to remove this photo from the event?"
        confirmLabel="Delete"
        confirmColor="error"
        loading={deletePhotoMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTargetId(null)}
      />
    </Box>
  );
}
